import React, { useMemo, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SxProps, Theme } from '@mui/material';
import { IconChevron } from '../../assets/icons';
import { DialogSelect, SelectOptionType } from '../dialog';
import { InputMui } from '../input-mui/input-mui';
import './index.scss';

type InputSelectProps<T> = {
  options: SelectOptionType<T>[];
  id?: string;
  name?: string;
  label: string;
  hiddenLabel?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  onChange?: (value: T) => void;
  icon?: ReactNode;
  openDialog?: boolean;
  setOpenDialog?: (show: boolean) => void;
  sx?: SxProps<Theme>;
  reverse?: boolean;
  value?: T;
};

export const InputSelect = <T extends string | number | readonly string[] | undefined | null>({
  options,
  name,
  label,
  hiddenLabel = false,
  disabled = false,
  onChange,
  icon = <IconChevron direction="right" noSpace />,
  openDialog = false,
  errorMessage,
  setOpenDialog,
  sx,
  reverse,
  value,
}: InputSelectProps<T>) => {
  const { t } = useTranslation('common');
  const [showSelectDialog, setShowSelectDialog] = useState<boolean>(false);

  const option = useMemo(() => {
    return options.find((op) => op.value === value);
  }, [value, options]);

  useEffect(() => setShowSelectDialog(openDialog), [openDialog]);

  const onClick = () => setShowSelectDialog(true);

  const dialogOnChange = (value: T) => {
    setShowSelectDialog(false);
    onChange?.(value);
  };

  const setShow = (value: boolean) => {
    setShowSelectDialog(value);
    setOpenDialog?.(value);
  };

  return (
    <>
      <InputMui
        value={option?.label ? `${option.label}${option.intValue ? ` (${option.intValue})` : ''}` : t('select')}
        {...(label && !hiddenLabel ? { label } : {})}
        sx={sx}
        disabled={disabled}
        readOnly
        errorMessage={errorMessage}
        onClickInput={onClick}
        name={name}
        endIcon={icon}
        reverse={reverse}
      />

      <DialogSelect
        show={showSelectDialog && !disabled}
        setShow={setShow}
        title={label}
        options={options}
        value={value}
        onChange={dialogOnChange}
      />
    </>
  );
};
